import * as React from 'react';
import { Recommendation, RecommendationSkeleton } from '../../../components/Recommendation';
import { FavoriteButtonContainer } from '../../FavoriteButtonContainer';
import { LabelsContainer } from '../../LabelsContainer';
import { RecommendationsList } from '../../../components/RecommendationsList';
import { useInView } from '@cian/valuation-utils-component';
import * as styles from './RecommendationsListMobile.css';
import { EPaginationStatus } from '../../../slices/recommendationsSlice';
import { PikPromoBannerContainer } from '../../PikPromoBannerContainer';

import type { useRecommendations } from '../useRecommendations';
import { ActionLink, UIText1 } from '@cian/ui-kit';

type TRecommendationsListMobileProps = {
  onTrack: (offerId: number | null, position: number, contextToken?: string) => void;
  recommendationsControls: ReturnType<typeof useRecommendations>;
  infiniteScroll?: boolean;
};

type TRecommendationCardsProps = Pick<
  ReturnType<typeof useRecommendations>,
  'recommendations' | 'onRecommendationClick' | 'onShowTrap' | 'isOfferVisited'
> & {
  onTrack: (offerId: number | null, position: number, contextToken?: string) => void;
};

const PIK_BANNER_POSITION = 20;

const RecommendationCards: React.FC<TRecommendationCardsProps> = ({
  recommendations,
  onTrack,
  onRecommendationClick,
  onShowTrap,
  isOfferVisited,
}) => (
  <>
    {recommendations.map((recommendation, index) => (
      <React.Fragment key={`recommendation-${index}`}>
        <Recommendation
          onTrack={onTrack}
          position={index + 1}
          onClick={onRecommendationClick}
          onShowTrap={onShowTrap}
          favoriteButton={<FavoriteButtonContainer size="XS" id={recommendation.offerId} />}
          labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
          {...recommendation}
        />
        {index === PIK_BANNER_POSITION - 1 && <PikPromoBannerContainer />}
      </React.Fragment>
    ))}
  </>
);

type TInfiniteScrollRecommendationsProps = Pick<
  ReturnType<typeof useRecommendations>,
  'onShow' | 'showHeader' | 'hasMore' | 'paginationStatus' | 'onLoadMore' | 'onRetryLoadMore'
> &
  TRecommendationCardsProps;

const InfiniteScrollRecommendations: React.FC<TInfiniteScrollRecommendationsProps> = ({
  onShow,
  showHeader,
  hasMore,
  paginationStatus,
  onLoadMore,
  onRetryLoadMore,
  recommendations,
  onTrack,
  onRecommendationClick,
  onShowTrap,
  isOfferVisited,
}) => {
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: false,
  });
  const recommendationsCountRef = React.useRef(recommendations.length);

  const recommendationsMobileSkeleton = React.useMemo(
    () =>
      new Array(8)
        .fill(0)
        .map((_, index) => <RecommendationSkeleton key={`recommendation-mobile-pagination-skeleton-${index}`} />),
    [],
  );

  React.useEffect(() => {
    if (inView && hasMore && paginationStatus === EPaginationStatus.Idle) {
      onLoadMore();
    }
  }, [hasMore, inView, onLoadMore, paginationStatus]);

  React.useEffect(() => {
    const previousRecommendationsCount = recommendationsCountRef.current;

    if (recommendations.length > previousRecommendationsCount && previousRecommendationsCount > 0) {
      onShow();
    }

    recommendationsCountRef.current = recommendations.length;
  }, [recommendations.length, onShow]);

  return (
    <RecommendationsList onShow={onShow} withHeader={showHeader}>
      <RecommendationCards
        recommendations={recommendations}
        onTrack={onTrack}
        onRecommendationClick={onRecommendationClick}
        onShowTrap={onShowTrap}
        isOfferVisited={isOfferVisited}
      />
      {paginationStatus === EPaginationStatus.Loading && recommendationsMobileSkeleton}
      {paginationStatus === EPaginationStatus.Error && (
        <div className={styles['pagination-error']}>
          <UIText1 color="text-secondary-default">Произошла ошибка</UIText1>
          <span className={styles['pagination-error-button']}>
            <ActionLink type="button" onClick={onRetryLoadMore} fontWeight="bold">
              Попробовать снова
            </ActionLink>
          </span>
        </div>
      )}
      {paginationStatus !== EPaginationStatus.Error && hasMore && (
        <div ref={ref} className={styles['pagination-sentinel']} />
      )}
    </RecommendationsList>
  );
};

export const RecommendationsListMobile: React.FC<TRecommendationsListMobileProps> = props => {
  const {
    recommendationsControls: {
      onShow,
      onMoreClick,
      showHeader,
      recommendations,
      onRecommendationClick,
      onShowTrap,
      isOfferVisited,
      hasMore,
      paginationStatus,
      onLoadMore,
      onRetryLoadMore,
    },
    onTrack,
    infiniteScroll,
  } = props;

  if (infiniteScroll) {
    return (
      <InfiniteScrollRecommendations
        onShow={onShow}
        showHeader={showHeader}
        hasMore={hasMore}
        paginationStatus={paginationStatus}
        onLoadMore={onLoadMore}
        onRetryLoadMore={onRetryLoadMore}
        recommendations={recommendations}
        onTrack={onTrack}
        onRecommendationClick={onRecommendationClick}
        onShowTrap={onShowTrap}
        isOfferVisited={isOfferVisited}
      />
    );
  }

  return (
    <RecommendationsList onShow={onShow} onMoreClick={onMoreClick} withHeader={showHeader} withBottomMargin>
      <RecommendationCards
        recommendations={recommendations}
        onTrack={onTrack}
        onRecommendationClick={onRecommendationClick}
        onShowTrap={onShowTrap}
        isOfferVisited={isOfferVisited}
      />
    </RecommendationsList>
  );
};
